import React, { useState, useEffect } from 'react';
import { groupBookingsService } from '../../../services/groupBookingsService';
import SecureGroupPaymentModal from './SecureGroupPaymentModal';
import { GROUP_TYPE_CFG, STATUS_CFG, PAY_STATUS_CFG } from './GroupsManagement';

// Voyager Pro reskin (Figma GroupsPage's GroupDrawer) - Details/Members/Payments/Notes
// tabs, matching Figma's DrawerTab layout instead of the old Overview/Participants/Payments split.

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
const fmtCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'members', label: 'Members' },
  { id: 'payments', label: 'Payments' },
  { id: 'notes', label: 'Notes' },
];

// Group booking lifecycle - matches STATUS_CFG's keys/order. Previously
// there was no UI anywhere to move a group past its 'planning' default (the
// backend's PUT /groups/:id already accepted `status` in its allowed-fields
// list, and the public signup page requires 'accepting'/'confirmed' -
// found during the full-CRM audit, 2026-09: every group ever created was
// permanently stuck in 'planning' and its signup link could never work).
const STATUS_ORDER = ['planning', 'accepting', 'confirmed', 'traveling', 'completed', 'cancelled'];

const GroupDetails = ({ group, onClose, onUpdate }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showSecurePaymentModal, setShowSecurePaymentModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(group.status);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');

  useEffect(() => { fetchGroupDetails(); setCurrentStatus(group.status); /* eslint-disable-next-line */ }, [group.id]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getById(group.id);
      setParticipants(data.participants || []);
    } catch (error) {
      console.error('Error fetching group details:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaymentDetails = (participant) => {
    setSelectedParticipant(participant);
    setShowSecurePaymentModal(true);
  };

  const handleStatusChange = async (newStatus) => {
    const previousStatus = currentStatus;
    setCurrentStatus(newStatus); // optimistic
    setSavingStatus(true);
    setStatusError('');
    try {
      await groupBookingsService.update(group.id, { status: newStatus });
      onUpdate?.();
    } catch (error) {
      console.error('Error updating group status:', error);
      setCurrentStatus(previousStatus);
      setStatusError('Failed to update status. Please try again.');
    } finally {
      setSavingStatus(false);
    }
  };

  const packages = typeof group.packages === 'string' ? JSON.parse(group.packages || '[]') : (group.packages || []);
  const paymentSchedule = typeof group.payment_schedule === 'string' ? JSON.parse(group.payment_schedule || '[]') : (group.payment_schedule || []);
  const coordinator = typeof group.coordinator === 'string' ? JSON.parse(group.coordinator || '{}') : (group.coordinator || {});
  const typeCfg = GROUP_TYPE_CFG[group.component_type] || { label: group.component_type, color: '#6B7A94', bg: 'rgba(107,122,148,0.08)', icon: '📋' };
  const statusCfg = STATUS_CFG[currentStatus] || STATUS_CFG.planning;
  const totalRevenue = participants.reduce((sum, p) => sum + parseFloat(p.amount_paid || 0), 0);

  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[460px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{group.title}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{group.destination} · {fmtDate(group.start_date)} – {fmtDate(group.end_date)}</div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative inline-flex items-center">
            <span className={`absolute left-2.5 w-1.5 h-1.5 rounded-full pointer-events-none ${statusCfg.dot}`} />
            <select
              value={currentStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={savingStatus}
              className={`pl-5 pr-6 py-1 rounded-full text-xs font-semibold border appearance-none cursor-pointer disabled:opacity-60 ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}
            >
              {STATUS_ORDER.map(s => (
                <option key={s} value={s}>{STATUS_CFG[s].label}</option>
              ))}
            </select>
          </div>
          {statusError && <span className="text-xs text-red-600">{statusError}</span>}
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}>
            {typeCfg.icon} {typeCfg.label}
          </span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${activeTab === tab.id ? 'text-white' : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)]'}`}
              style={activeTab === tab.id ? { backgroundColor: 'var(--primary)' } : {}}
            >
              {tab.label}{tab.id === 'members' ? ` (${participants.length})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {activeTab === 'details' && (
          <>
            <div>
              <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Trip Details</h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Capacity</span><span className="font-medium text-[var(--foreground)]">{group.current_participants || participants.length}{group.max_participants ? ` / ${group.max_participants}` : ''}</span></div>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total Revenue</span><span className="font-medium text-[var(--foreground)]">{fmtCurrency(totalRevenue)}</span></div>
              </div>
            </div>

            {packages.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Packages</h3>
                <div className="space-y-2">
                  {packages.map((pkg, i) => (
                    <div key={i} className="p-3 bg-[var(--muted)]/40 rounded-xl">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-semibold text-[var(--foreground)]">{pkg.name}</div>
                        <div className="text-xs font-semibold text-emerald-700">{fmtCurrency(pkg.price)}/person</div>
                      </div>
                      {pkg.description && <p className="text-xs text-[var(--muted-foreground)]">{pkg.description}</p>}
                      {pkg.inclusions?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {pkg.inclusions.map((inc, j) => <span key={j} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700">{inc}</span>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {coordinator.name && (
              <div>
                <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Group Coordinator</h3>
                <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm space-y-1">
                  <div className="font-medium text-[var(--foreground)]">{coordinator.name}</div>
                  {coordinator.email && <div className="text-[var(--muted-foreground)]">{coordinator.email}</div>}
                  {coordinator.phone && <div className="text-[var(--muted-foreground)]">{coordinator.phone}</div>}
                </div>
              </div>
            )}

            {group.cancellation_policy && (
              <div>
                <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Cancellation Policy</h3>
                <p className="text-sm text-[var(--foreground)]">{group.cancellation_policy}</p>
              </div>
            )}

            {group.public_url && (
              <div>
                <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Signup Link</h3>
                <div className="flex gap-2">
                  <input type="text" value={group.public_url} readOnly className="flex-1 px-3 py-2 text-xs border border-[var(--border)] rounded-lg bg-[var(--muted)]/40 text-[var(--foreground)]" />
                  <button
                    onClick={() => { navigator.clipboard?.writeText(group.public_url); }}
                    className="px-3 py-2 rounded-lg text-xs font-semibold text-white hover:brightness-105 transition-all flex-shrink-0"
                    style={{ backgroundColor: 'var(--accent)' }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'members' && (
          loading ? (
            <div className="text-center py-10 text-sm text-[var(--muted-foreground)]">Loading members…</div>
          ) : participants.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No members yet</div>
              <div className="text-xs text-[var(--muted-foreground)]">Share the signup link to get participants to join.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map(p => {
                const payCfg = PAY_STATUS_CFG[p.payment_status] || PAY_STATUS_CFG.unpaid;
                return (
                  <div key={p.id} className="p-3 bg-[var(--muted)]/40 rounded-xl">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-sm font-semibold text-[var(--foreground)] truncate">
                        {p.first_name} {p.last_name}{p.is_organizer ? ' 👑' : ''}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${payCfg.bg} ${payCfg.color}`}>{payCfg.label}</span>
                    </div>
                    <div className="text-xs text-[var(--muted-foreground)] truncate">{p.email}</div>
                    <div className="flex items-center justify-between mt-1.5 text-xs text-[var(--muted-foreground)]">
                      <span>{p.package_selected || '—'} · {(p.adults ?? 1)} adult{(p.adults ?? 1) > 1 ? 's' : ''}{p.children > 0 ? `, ${p.children} child${p.children > 1 ? 'ren' : ''}` : ''}{p.room_type ? ` · ${p.room_type}` : ''}</span>
                      <button onClick={() => handleViewPaymentDetails(p)} className="font-semibold hover:underline flex-shrink-0" style={{ color: 'var(--accent)' }}>View Payments</button>
                    </div>
                    <div className="text-xs font-semibold text-[var(--foreground)] mt-1">{fmtCurrency(p.amount_paid)} / {fmtCurrency(p.total_amount)}</div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'payments' && (
          <>
            {paymentSchedule.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Payment Schedule</h3>
                <div className="space-y-2">
                  {paymentSchedule.map((p, i) => (
                    <div key={i} className="p-3 bg-[var(--muted)]/40 rounded-xl flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium text-[var(--foreground)]">{p.name}</div>
                        <div className="text-xs text-[var(--muted-foreground)]">{p.percentage}% of total{p.due_date ? ` · Due ${fmtDate(p.due_date)}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Payment Summary</h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total Revenue</span><span className="font-semibold text-emerald-700">{fmtCurrency(totalRevenue)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total Members</span><span className="font-medium text-[var(--foreground)]">{participants.length}</span></div>
                <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Avg. per Member</span><span className="font-semibold text-emerald-700">{fmtCurrency(participants.length > 0 ? totalRevenue / participants.length : 0)}</span></div>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">By Status</h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(PAY_STATUS_CFG).map(key => (
                  <div key={key} className="p-2.5 bg-[var(--muted)]/40 rounded-xl flex items-center justify-between">
                    <span className={`text-xs font-semibold ${PAY_STATUS_CFG[key].color}`}>{PAY_STATUS_CFG[key].label}</span>
                    <span className="text-sm font-bold text-[var(--foreground)]">{participants.filter(p => p.payment_status === key).length}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'notes' && (
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Agent Notes</h3>
            {group.notes ? (
              <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)] whitespace-pre-wrap">{group.notes}</div>
            ) : (
              <div className="text-sm text-[var(--muted-foreground)]">No notes yet.</div>
            )}
          </div>
        )}
      </div>

      {showSecurePaymentModal && selectedParticipant && (
        <SecureGroupPaymentModal participant={selectedParticipant} onClose={() => { setSelectedParticipant(null); setShowSecurePaymentModal(false); }} />
      )}
    </div>
  );
};

export default GroupDetails;
